/**
 * TrustlinesPanel
 *
 * A compact, modern "trustlines" section for the profile overlay.
 * Covers the full Stellar-SDK surface the user asked for:
 *
 *   • view      — Horizon account balances, issuer, limit, "buying
 *                 liabilities" + "selling liabilities" (so users can
 *                 see how much of their balance is actually available).
 *   • add       — ChangeTrust op (opens a modal to type asset code +
 *                 issuer, optional custom limit).
 *   • remove    — ChangeTrust op with limit=0 (only offered when the
 *                 line is empty and unlocked; Stellar rejects otherwise).
 *   • pay       — Payment op to the other wallet, per-line "Send" button.
 *
 * The panel is intentionally viewer-aware:
 *
 *   • `isOwner = true`  → the user is looking at their own profile.
 *     All write buttons (add / remove / pay) are enabled as long as a
 *     `wallet` signer is supplied.  When no signer is supplied (e.g.
 *     the user is browsing in read-only mode) the panel silently
 *     downgrades to view-only.
 *   • `isOwner = false` → the panel shows the OTHER user's balances.
 *     The only write surface exposed is the per-asset "Send" button,
 *     which opens a Payment modal that pulls the amount out of the
 *     VIEWER's own account — so the button needs the viewer's signer
 *     (passed as `viewerWallet`) but does NOT need the subject to be
 *     online.
 *
 * All submissions go through Horizon directly (no Soroban fee-payer
 * needed — these are classic Stellar operations, which Freighter and
 * WalletConnect both sign without any smart-account plumbing).  Smart
 * accounts (C... addresses) can't hold classic trustlines, so we hide
 * the write surface for them and show an explanatory note.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Horizon,
  Asset,
  Networks,
  Operation,
  TransactionBuilder,
  BASE_FEE,
  Memo,
} from '@stellar/stellar-sdk';

export interface TrustlinesWallet {
  getPublicKey(): Promise<string>;
  signTransaction(xdr: string, network: Networks | string): Promise<string>;
}

export interface TrustlineRow {
  asset_type: string;
  asset_code?: string;
  asset_issuer?: string;
  balance: string;
  limit?: string;
  buying_liabilities?: string;
  selling_liabilities?: string;
  is_authorized?: boolean;
}

export interface TrustlinesPanelProps {
  /** Stellar G... address of the profile being viewed. */
  subjectAddress: string;
  /** `true` if the viewer IS the subject (my profile). */
  isOwner: boolean;
  /** Signer for the subject (owner-only writes: add / remove). */
  ownerWallet?: TrustlinesWallet;
  /** Signer for the viewer (payments out of the viewer's account). */
  viewerWallet?: TrustlinesWallet;
  /** Passed through to Horizon — defaults to testnet. */
  horizonUrl?: string;
  network?: Networks;
  /** Called after any on-chain change so the parent can refresh. */
  onMutated?: () => void;
}

type ModalKind = null | 'add' | 'remove' | 'pay';

// ── Helpers ──────────────────────────────────────────────────────────────────

function resolveHorizon(horizonUrl?: string, network?: Networks): { server: Horizon.Server; net: Networks } {
  const net = network ?? Networks.TESTNET;
  const url = horizonUrl
    ?? (net === Networks.PUBLIC ? 'https://horizon.stellar.org' : 'https://horizon-testnet.stellar.org');
  return { server: new Horizon.Server(url), net };
}

function isSmartAccount(addr: string): boolean {
  return /^C[A-Z0-9]{55}$/.test(addr);
}

function fmtAmount(s: string | undefined): string {
  if (!s) return '0';
  const n = parseFloat(s);
  if (!Number.isFinite(n)) return s;
  if (Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 7 });
}

function assetLabel(line: TrustlineRow): string {
  if (line.asset_type === 'native') return 'XLM';
  return line.asset_code || '???';
}

function assetKey(line: TrustlineRow): string {
  return line.asset_type === 'native' ? 'native' : `${line.asset_code}:${line.asset_issuer}`;
}

function shortAddr(addr?: string): string {
  if (!addr) return '';
  return addr.length > 12 ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : addr;
}

function buildAssetFrom(line: TrustlineRow): Asset {
  if (line.asset_type === 'native') return Asset.native();
  if (!line.asset_code || !line.asset_issuer) throw new Error('Missing asset code/issuer');
  return new Asset(line.asset_code, line.asset_issuer);
}

// ── Component ────────────────────────────────────────────────────────────────

export const TrustlinesPanel: React.FC<TrustlinesPanelProps> = ({
  subjectAddress,
  isOwner,
  ownerWallet,
  viewerWallet,
  horizonUrl,
  network,
  onMutated,
}) => {
  const { server, net } = useMemo(() => resolveHorizon(horizonUrl, network), [horizonUrl, network]);
  const [lines, setLines] = useState<TrustlineRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalKind>(null);
  const [modalLine, setModalLine] = useState<TrustlineRow | null>(null);
  const [busy, setBusy] = useState(false);

  const smartAccount = isSmartAccount(subjectAddress);

  const refresh = useCallback(async () => {
    if (!subjectAddress) return;
    setLoading(true);
    setError(null);
    try {
      if (smartAccount) {
        setLines([]);
        return;
      }
      const acct = await server.loadAccount(subjectAddress);
      const rows: TrustlineRow[] = (acct.balances as any[]).map((b) => ({
        asset_type: b.asset_type,
        asset_code: b.asset_code,
        asset_issuer: b.asset_issuer,
        balance: b.balance,
        limit: b.limit,
        buying_liabilities: b.buying_liabilities,
        selling_liabilities: b.selling_liabilities,
        is_authorized: b.is_authorized,
      }));
      setLines(rows);
    } catch (err: any) {
      if (String(err?.response?.status) === '404') {
        setLines([]);
        setError('Account not found on Horizon — it may be unfunded.');
      } else {
        setError(err?.message || 'Failed to load trustlines.');
      }
    } finally {
      setLoading(false);
    }
  }, [server, subjectAddress, smartAccount]);

  useEffect(() => { void refresh(); }, [refresh]);

  // ── Write actions ──────────────────────────────────────────────────────────

  const submitChangeTrust = useCallback(async (opts: { asset: Asset; limit?: string }) => {
    if (!ownerWallet) throw new Error('Wallet signer required for this action.');
    setBusy(true);
    try {
      const source = await server.loadAccount(subjectAddress);
      const tx = new TransactionBuilder(source, { fee: BASE_FEE, networkPassphrase: net })
        .addOperation(Operation.changeTrust({ asset: opts.asset, limit: opts.limit }))
        .setTimeout(60)
        .build();
      const signedXdr = await ownerWallet.signTransaction(tx.toXDR(), net);
      const signed = TransactionBuilder.fromXDR(signedXdr, net);
      await server.submitTransaction(signed as any);
      setModal(null);
      setModalLine(null);
      await refresh();
      onMutated?.();
    } finally {
      setBusy(false);
    }
  }, [ownerWallet, server, subjectAddress, net, refresh, onMutated]);

  const submitPayment = useCallback(async (opts: {
    destination: string;
    asset: Asset;
    amount: string;
    memo?: string;
  }) => {
    if (!viewerWallet) throw new Error('Wallet signer required to send a payment.');
    setBusy(true);
    try {
      const sender = await viewerWallet.getPublicKey();
      const source = await server.loadAccount(sender);
      let builder = new TransactionBuilder(source, { fee: BASE_FEE, networkPassphrase: net })
        .addOperation(Operation.payment({
          destination: opts.destination,
          asset: opts.asset,
          amount: opts.amount,
        }));
      if (opts.memo) builder = builder.addMemo(Memo.text(opts.memo.slice(0, 28)));
      const tx = builder.setTimeout(60).build();
      const signedXdr = await viewerWallet.signTransaction(tx.toXDR(), net);
      const signed = TransactionBuilder.fromXDR(signedXdr, net);
      await server.submitTransaction(signed as any);
      setModal(null);
      setModalLine(null);
      await refresh();
      onMutated?.();
    } finally {
      setBusy(false);
    }
  }, [viewerWallet, server, net, refresh, onMutated]);

  // ── Render ─────────────────────────────────────────────────────────────────

  const canOwnerWrite   = isOwner && !!ownerWallet && !smartAccount;
  const canViewerPay    = !!viewerWallet && !smartAccount;

  return (
    <div className="trustlines-panel" style={panelStyle}>
      <div style={headerStyle}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Trustlines & Balances</div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>
            {shortAddr(subjectAddress)} · {net === Networks.PUBLIC ? 'mainnet' : 'testnet'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canOwnerWrite && (
            <button style={btnGhost} onClick={() => setModal('add')}>+ Add trustline</button>
          )}
          <button style={btnGhost} onClick={() => void refresh()} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {smartAccount && (
        <div style={noteStyle}>
          This is a smart-contract account (C…); classic Stellar trustlines
          don't apply. Use the vault/deposit UI for smart-account balances.
        </div>
      )}
      {error && <div style={{ ...noteStyle, color: '#b91c1c' }}>{error}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
        {lines.length === 0 && !loading && !smartAccount && !error && (
          <div style={{ fontSize: 12, color: '#6b7280' }}>No trustlines on this account.</div>
        )}
        {lines.map((line) => {
          const label = assetLabel(line);
          const canRemove = canOwnerWrite && line.asset_type !== 'native'
            && parseFloat(line.balance) === 0
            && parseFloat(line.buying_liabilities || '0') === 0
            && parseFloat(line.selling_liabilities || '0') === 0;
          return (
            <div key={assetKey(line)} style={rowStyle}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontWeight: 700, fontSize: 14 }}>{label}</span>
                  {line.asset_issuer && (
                    <span style={{ fontSize: 11, color: '#6b7280' }} title={line.asset_issuer}>
                      {shortAddr(line.asset_issuer)}
                    </span>
                  )}
                  {line.is_authorized === false && (
                    <span style={badgeWarn}>unauthorized</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>
                  Available {fmtAmount(String(
                    Math.max(0, parseFloat(line.balance) - parseFloat(line.selling_liabilities || '0'))
                  ))}
                  {line.limit && line.asset_type !== 'native' ? ` · limit ${fmtAmount(line.limit)}` : ''}
                </div>
              </div>

              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{fmtAmount(line.balance)}</div>
              </div>

              <div style={{ display: 'flex', gap: 6, marginLeft: 10 }}>
                {canViewerPay && (
                  <button
                    style={btnPrimary}
                    onClick={() => { setModalLine(line); setModal('pay'); }}
                    title={isOwner ? 'Send to someone' : `Send ${label} to ${shortAddr(subjectAddress)}`}
                  >
                    Send
                  </button>
                )}
                {canRemove && (
                  <button
                    style={btnDanger}
                    onClick={() => { setModalLine(line); setModal('remove'); }}
                    title="Remove trustline (requires zero balance)"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Modals ────────────────────────────────────────────────────────── */}
      {modal === 'add' && (
        <AddTrustlineModal
          busy={busy}
          onCancel={() => setModal(null)}
          onSubmit={(p) => submitChangeTrust({ asset: new Asset(p.code, p.issuer), limit: p.limit })}
        />
      )}
      {modal === 'remove' && modalLine && (
        <ConfirmModal
          busy={busy}
          title="Remove trustline"
          message={`Set the limit on ${assetLabel(modalLine)} to 0?  This can only be undone by creating a new trustline.`}
          onCancel={() => { setModal(null); setModalLine(null); }}
          onConfirm={() => submitChangeTrust({ asset: buildAssetFrom(modalLine), limit: '0' })}
        />
      )}
      {modal === 'pay' && modalLine && (
        <PaymentModal
          busy={busy}
          line={modalLine}
          destination={subjectAddress}
          defaultMemo={isOwner ? '' : 'gift'}
          canEditDestination={isOwner}
          onCancel={() => { setModal(null); setModalLine(null); }}
          onSubmit={(p) => submitPayment({
            destination: p.destination,
            asset: buildAssetFrom(modalLine),
            amount: p.amount,
            memo: p.memo,
          })}
        />
      )}
    </div>
  );
};

// ── Modals ──────────────────────────────────────────────────────────────────

const AddTrustlineModal: React.FC<{
  busy: boolean;
  onCancel: () => void;
  onSubmit: (p: { code: string; issuer: string; limit?: string }) => Promise<void> | void;
}> = ({ busy, onCancel, onSubmit }) => {
  const [code, setCode] = useState('');
  const [issuer, setIssuer] = useState('');
  const [limit, setLimit] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const handle = async () => {
    setErr(null);
    if (!code || code.length > 12) return setErr('Asset code is required (1–12 chars).');
    if (!/^G[A-Z0-9]{55}$/.test(issuer)) return setErr('Issuer must be a valid G… public key.');
    if (limit && isNaN(Number(limit))) return setErr('Limit must be numeric.');
    try {
      await onSubmit({ code, issuer, limit: limit || undefined });
    } catch (e: any) {
      setErr(e?.message || 'Failed to add trustline.');
    }
  };

  return (
    <Modal title="Add trustline" onCancel={onCancel}>
      <Field label="Asset code">
        <input style={inputStyle} value={code} onChange={(e) => setCode(e.target.value.trim())} placeholder="e.g. USDC" />
      </Field>
      <Field label="Issuer public key">
        <input style={inputStyle} value={issuer} onChange={(e) => setIssuer(e.target.value.trim())} placeholder="G…" />
      </Field>
      <Field label="Limit (optional — default max)">
        <input style={inputStyle} value={limit} onChange={(e) => setLimit(e.target.value.trim())} placeholder="max" />
      </Field>
      {err && <div style={errStyle}>{err}</div>}
      <div style={modalButtonsStyle}>
        <button style={btnGhost} onClick={onCancel} disabled={busy}>Cancel</button>
        <button style={btnPrimary} onClick={handle} disabled={busy}>{busy ? 'Submitting…' : 'Add'}</button>
      </div>
    </Modal>
  );
};

const ConfirmModal: React.FC<{
  busy: boolean;
  title: string;
  message: string;
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
}> = ({ busy, title, message, onCancel, onConfirm }) => {
  const [err, setErr] = useState<string | null>(null);
  const handle = async () => {
    setErr(null);
    try { await onConfirm(); } catch (e: any) { setErr(e?.message || 'Failed'); }
  };
  return (
    <Modal title={title} onCancel={onCancel}>
      <div style={{ fontSize: 13, lineHeight: 1.45 }}>{message}</div>
      {err && <div style={errStyle}>{err}</div>}
      <div style={modalButtonsStyle}>
        <button style={btnGhost} onClick={onCancel} disabled={busy}>Cancel</button>
        <button style={btnDanger} onClick={handle} disabled={busy}>{busy ? 'Submitting…' : 'Confirm'}</button>
      </div>
    </Modal>
  );
};

const PaymentModal: React.FC<{
  busy: boolean;
  line: TrustlineRow;
  destination: string;
  defaultMemo?: string;
  canEditDestination: boolean;
  onCancel: () => void;
  onSubmit: (p: { destination: string; amount: string; memo?: string }) => Promise<void> | void;
}> = ({ busy, line, destination, defaultMemo, canEditDestination, onCancel, onSubmit }) => {
  const [dest, setDest] = useState(destination);
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState(defaultMemo || '');
  const [err, setErr] = useState<string | null>(null);

  const handle = async () => {
    setErr(null);
    if (!/^G[A-Z0-9]{55}$/.test(dest)) return setErr('Destination must be a valid G… public key.');
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) return setErr('Amount must be a positive number.');
    try {
      await onSubmit({ destination: dest, amount: String(amount), memo });
    } catch (e: any) {
      setErr(e?.message || 'Failed to send payment.');
    }
  };

  return (
    <Modal title={`Send ${assetLabel(line)}`} onCancel={onCancel}>
      <Field label="To">
        <input style={inputStyle} value={dest} onChange={(e) => setDest(e.target.value.trim())} readOnly={!canEditDestination} />
      </Field>
      <Field label={`Amount (${assetLabel(line)})`}>
        <input style={inputStyle} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
      </Field>
      <Field label="Memo (optional, 28 chars)">
        <input style={inputStyle} value={memo} onChange={(e) => setMemo(e.target.value)} maxLength={28} />
      </Field>
      {err && <div style={errStyle}>{err}</div>}
      <div style={modalButtonsStyle}>
        <button style={btnGhost} onClick={onCancel} disabled={busy}>Cancel</button>
        <button style={btnPrimary} onClick={handle} disabled={busy}>{busy ? 'Sending…' : 'Send'}</button>
      </div>
    </Modal>
  );
};

const Modal: React.FC<{ title: string; onCancel: () => void; children: React.ReactNode }> = ({ title, onCancel, children }) => (
  <div style={modalOverlayStyle} onClick={onCancel}>
    <div style={modalBoxStyle} onClick={(e) => e.stopPropagation()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{title}</div>
        <button style={btnGhost} onClick={onCancel} aria-label="Close">×</button>
      </div>
      {children}
    </div>
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ fontSize: 11, color: '#374151', marginBottom: 4, fontWeight: 600 }}>{label}</div>
    {children}
  </div>
);

// ── Styles ──────────────────────────────────────────────────────────────────

const panelStyle: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  background: '#fafafa',
  borderRadius: 10,
  padding: 12,
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  background: '#fff',
  border: '1px solid #eef2f7',
  borderRadius: 8,
  padding: '10px 12px',
};

const noteStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#4b5563',
  background: '#fff7ed',
  border: '1px solid #fed7aa',
  borderRadius: 8,
  padding: 8,
  marginTop: 8,
};

const btnPrimary: React.CSSProperties = {
  background: '#111827',
  color: '#fff',
  border: '1px solid #111827',
  borderRadius: 8,
  padding: '6px 12px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};

const btnGhost: React.CSSProperties = {
  background: '#fff',
  color: '#111827',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  padding: '6px 12px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};

const btnDanger: React.CSSProperties = {
  background: '#fff',
  color: '#b91c1c',
  border: '1px solid #fecaca',
  borderRadius: 8,
  padding: '6px 12px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};

const badgeWarn: React.CSSProperties = {
  fontSize: 10,
  background: '#fef3c7',
  color: '#92400e',
  padding: '2px 6px',
  borderRadius: 999,
};

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000,
};

const modalBoxStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  padding: 16,
  width: 420,
  maxWidth: '92vw',
  boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  padding: '8px 10px',
  fontSize: 13,
  fontFamily: 'inherit',
};

const errStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#b91c1c',
  marginTop: 6,
};

const modalButtonsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  marginTop: 12,
};
